/* Media loading helpers.

   1. Click-to-play facade for embedded video — the iframe is only created once
      a visitor presses play, so third-party player scripts never touch first
      paint.
   2. Background hero video — sources are only promoted on viewports large
      enough to justify the download, and never when the visitor has asked for
      reduced motion. Everywhere else the poster image stands in.
   3. Looping section video — autoplays muted (the only kind of autoplay a
      browser allows) with a real button so a visitor can turn sound on. */
(function () {
  function mountEmbed(button) {
    var frame = button.closest('.sf-media-card__frame, .sf-player__frame') || button.parentNode;

    // Hosted video already rendered hidden in the frame: reveal and play it.
    if (button.hasAttribute('data-sf-reveal')) {
      var video = frame.querySelector('video[hidden]');
      if (!video) return;
      video.hidden = false;
      button.remove();
      frame.classList.add('sf-frame--playing');
      var playing = video.play();
      if (playing && typeof playing.catch === 'function') playing.catch(function () {});
      video.focus();
      return;
    }

    var src = button.getAttribute('data-sf-embed');
    if (!src) return;

    var iframe = document.createElement('iframe');

    iframe.setAttribute('src', src);
    iframe.setAttribute('title', button.getAttribute('data-sf-title') || 'Video');
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');

    button.remove();
    frame.classList.add('sf-frame--playing');
    frame.appendChild(iframe);
    iframe.focus();
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.sf-play');
    if (button) mountEmbed(button);
  });

  function startBackgroundVideo(video) {
    if (video.dataset.sfLoaded) return;

    var sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return;

    video.dataset.sfLoaded = 'true';

    for (var i = 0; i < sources.length; i++) {
      sources[i].setAttribute('src', sources[i].getAttribute('data-src'));
      sources[i].removeAttribute('data-src');
    }

    video.load();

    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      // Autoplay can still be refused (data saver, battery saver). The poster
      // is already painted underneath, so there is nothing to clean up.
      attempt.catch(function () {});
    }

    // WCAG 2.2.2. The control is only worth showing once the clip is genuinely
    // moving — on a phone, or under reduced motion, this never runs and the
    // hero stays exactly as it was.
    revealMotionToggle(video);
  }

  /* Background video pause control.

     Kept beside startBackgroundVideo rather than in the loop-video block
     because the hero video has no sound and no other controls: pause is the
     only thing a visitor can do to it. */
  function heroToggleFor(video) {
    var scope = video.closest('.sf-hero') || document;
    return scope.querySelector('[data-sf-bg-toggle]');
  }

  function paintMotionToggle(video) {
    var button = heroToggleFor(video);
    if (!button) return;

    var paused = video.paused;
    button.setAttribute('aria-pressed', paused ? 'true' : 'false');
    button.setAttribute('aria-label', paused ? 'Play background video' : 'Pause background video');
    if (paused) button.classList.add('is-paused');
    else button.classList.remove('is-paused');
  }

  function revealMotionToggle(video) {
    var button = heroToggleFor(video);
    if (button) {
      button.hidden = false;
      paintMotionToggle(video);
    }

    var sound = heroAudioFor(video);
    if (sound) {
      sound.hidden = false;
      paintHeroAudio(video);
    }
  }

  /* Sound for the hero clip. Separate from the loop-video audio toggle because
     that one lives inside a card and this one shares the hero's control cluster,
     but the contract is the same: muted at all times until a visitor asks. */
  function heroAudioFor(video) {
    var scope = video.closest('.sf-hero') || document;
    return scope.querySelector('[data-sf-bg-audio]');
  }

  function paintHeroAudio(video) {
    var button = heroAudioFor(video);
    if (!button) return;

    var on = !video.muted;
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    button.setAttribute('aria-label', on ? 'Turn background video sound off' : 'Turn background video sound on');
    if (on) button.classList.add('is-unmuted');
    else button.classList.remove('is-unmuted');
  }

  function toggleHeroAudio(button) {
    var scope = button.closest('.sf-hero') || document;
    var video = scope.querySelector('video[data-sf-bg-video]');
    if (!video) return;

    video.muted = !video.muted;
    // Unmuting a paused clip should start it, or the button appears to do
    // nothing at all.
    if (!video.muted && video.paused) {
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
    }
    paintHeroAudio(video);
  }

  function toggleBackgroundVideo(button) {
    var scope = button.closest('.sf-hero') || document;
    var video = scope.querySelector('video[data-sf-bg-video]');
    if (!video) return;

    if (video.paused) {
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
    } else {
      video.pause();
    }

    paintMotionToggle(video);
  }

  function initBackgroundVideo() {
    var videos = document.querySelectorAll('video[data-sf-bg-video]');
    if (!videos.length) return;

    var bigEnough = window.matchMedia('(min-width: 750px)');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    function evaluate() {
      if (!bigEnough.matches || reduced.matches) return;
      for (var i = 0; i < videos.length; i++) startBackgroundVideo(videos[i]);
    }

    evaluate();

    if (typeof bigEnough.addEventListener === 'function') {
      bigEnough.addEventListener('change', evaluate);
      reduced.addEventListener('change', evaluate);
    }
  }

  /* Looping video sound toggle.

     A looping video may only autoplay while it is muted, so the markup ships
     muted and this button is the visitor's only route to audio. State lives on
     the video element itself — aria-pressed and aria-label are rewritten to
     match it, so the control is honest to a screen reader and not just to CSS. */
  var LOOP_SELECTOR = '[data-sf-loop]';

  function loopVideo(container) {
    return container ? container.querySelector('video') : null;
  }

  function playQuietly(video) {
    var attempt = video.play();
    // Same swallow as the reveal handler above: a refused play() is a browser
    // policy decision, not a failure we can do anything about.
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  }

  function paintLoopAudio(container) {
    var video = loopVideo(container);
    var button = container.querySelector('[data-sf-audio-toggle]');
    if (!video || !button) return;

    var soundOn = !video.muted;

    if (soundOn) container.classList.add('is-unmuted');
    else container.classList.remove('is-unmuted');

    button.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
    button.setAttribute('aria-label', soundOn ? 'Turn sound off' : 'Turn sound on');
  }

  function muteOtherLoopVideos(current) {
    var containers = document.querySelectorAll(LOOP_SELECTOR);

    for (var i = 0; i < containers.length; i++) {
      if (containers[i] === current) continue;

      var other = loopVideo(containers[i]);
      // Two soundtracks at once is always a bug, so unmuting one loop mutes
      // every other loop on the page rather than layering over it.
      if (other && !other.muted) other.muted = true;
      paintLoopAudio(containers[i]);
    }
  }

  /* Looping video pause control — WCAG 2.2.2.

     The sound toggle was previously the only control on the clip, and unmuting
     is not stopping. State is read back off the element rather than tracked
     separately, so the label stays honest if playback ends for any other
     reason (tab backgrounded, source error). */
  function paintLoopMotion(container) {
    var video = loopVideo(container);
    var button = container.querySelector('[data-sf-play-toggle]');
    if (!video || !button) return;

    var paused = video.paused;
    button.setAttribute('aria-pressed', paused ? 'true' : 'false');
    button.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    if (paused) container.classList.add('is-paused');
    else container.classList.remove('is-paused');
  }

  function toggleLoopMotion(button) {
    var container = button.closest(LOOP_SELECTOR);
    if (!container) return;

    var video = loopVideo(container);
    if (!video) return;

    if (video.paused) {
      // A reduced-motion visitor was never autoplayed, and the file may still
      // be held back by preload="none" — promote it before asking it to play.
      if (!video.dataset.sfLoaded) {
        video.dataset.sfLoaded = '1';
        video.preload = 'auto';
        video.load();
      }
      playQuietly(video);
    } else {
      video.pause();
    }

    paintLoopMotion(container);
  }

  function toggleLoopAudio(button) {
    var container = button.closest(LOOP_SELECTOR);
    if (!container) return;

    var video = loopVideo(container);
    if (!video) return;

    var turningSoundOn = video.muted;
    video.muted = !video.muted;

    if (turningSoundOn) {
      muteOtherLoopVideos(container);
      // Some browsers pause on unmute, and a reduced-motion visitor was never
      // autoplayed at all — either way this is the deliberate press that starts it.
      playQuietly(video);
    }

    paintLoopAudio(container);
  }

  // The looping video is by far the heaviest asset on the site — 4.1 MB, and
  // it was downloading on page load for every visitor whether they ever
  // scrolled to it or not. It now ships preload="none" and only fetches when
  // it is about to enter the viewport, which takes it off the critical path.
  function lazyLoadLoopVideos() {
    var containers = document.querySelectorAll(LOOP_SELECTOR);
    if (!containers.length) return;

    var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function start(video) {
      if (video.dataset.sfLoaded) return;
      if (reducedMotion.matches) return;
      video.dataset.sfLoaded = "1";
      video.preload = "auto";
      video.autoplay = true;
      video.load();
      playQuietly(video);
    }

    if (!("IntersectionObserver" in window)) {
      for (var i = 0; i < containers.length; i++) {
        var v = loopVideo(containers[i]);
        if (v) start(v);
      }
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var video = loopVideo(entry.target);
          if (video) start(video);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: "200px 0px" }
    );

    for (var j = 0; j < containers.length; j++) io.observe(containers[j]);
  }
  function syncLoopVideos() {
    var containers = document.querySelectorAll(LOOP_SELECTOR);
    if (!containers.length) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    for (var i = 0; i < containers.length; i++) {
      var video = loopVideo(containers[i]);
      if (!video) continue;

      if (reduced.matches) {
        // Honour the OS setting the same way the hero does: no unasked-for
        // motion. The button still works, so sound and playback stay available.
        video.removeAttribute('autoplay');
        video.autoplay = false;
        if (!video.paused) video.pause();
      }

      paintLoopAudio(containers[i]);
      paintLoopMotion(containers[i]);
    }
  }

  function initLoopVideoAudio() {
    // Bind once. Delegation off document also covers containers injected later
    // (theme editor section reloads), so nothing needs rebinding per node.
    if (!document.documentElement.dataset.sfLoopAudio) {
      document.documentElement.dataset.sfLoopAudio = 'true';

      document.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || !target.closest) return;

        // Native <button>s, so Enter and Space arrive here as clicks already.
        var audio = target.closest('[data-sf-audio-toggle]');
        if (audio) return toggleLoopAudio(audio);

        var motion = target.closest('[data-sf-play-toggle]');
        if (motion) return toggleLoopMotion(motion);

        var heroSound = target.closest('[data-sf-bg-audio]');
        if (heroSound) return toggleHeroAudio(heroSound);

        var hero = target.closest('[data-sf-bg-toggle]');
        if (hero) return toggleBackgroundVideo(hero);
      });

      // Playback can also stop without a press — a backgrounded tab, a source
      // error, the end of a non-looping clip. Repaint from the element so the
      // control never claims a state the video is not in.
      document.addEventListener(
        'play',
        function (event) {
          var container = event.target.closest ? event.target.closest(LOOP_SELECTOR) : null;
          if (container) paintLoopMotion(container);
          else if (event.target.hasAttribute && event.target.hasAttribute('data-sf-bg-video')) {
            paintMotionToggle(event.target);
          }
        },
        true
      );

      document.addEventListener(
        'pause',
        function (event) {
          var container = event.target.closest ? event.target.closest(LOOP_SELECTOR) : null;
          if (container) paintLoopMotion(container);
          else if (event.target.hasAttribute && event.target.hasAttribute('data-sf-bg-video')) {
            paintMotionToggle(event.target);
          }
        },
        true
      );

      document.addEventListener('shopify:section:load', syncLoopVideos);

      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (typeof reduced.addEventListener === 'function') {
        reduced.addEventListener('change', syncLoopVideos);
      }
    }

    syncLoopVideos();
    lazyLoadLoopVideos();
  }

  function init() {
    initBackgroundVideo();
    initLoopVideoAudio();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
